import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SchoolSetupPage from "@/pages/SchoolSetupPage";
import AdminDashboard from "@/pages/AdminDashboard";
import WardenDashboard from "@/pages/WardenDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import SuperAdminPortal from "@/pages/SuperAdminPortal";
import SystemHealth from "@/components/SystemHealth";
import { Toaster } from "@/components/ui/sonner";
import { Helmet } from "react-helmet";

const BACKEND_URL = process.env.REACT_APP_API_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem("hostelconnect_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("hostelconnect_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("hostelconnect_user");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <>
      <Helmet>
        <title>{process.env.REACT_APP_NAME}</title>
        <link rel="icon" href="/favicon.webp" type="image/webp" />
      </Helmet>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/system-health" element={<SystemHealth />} />

              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute role="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/warden/*"
                element={
                  <ProtectedRoute role="warden">
                    <WardenDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/parent/*"
                element={
                  <ProtectedRoute role="parent">
                    <ParentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/school-setup" element={<SchoolSetupPage />} />
              <Route path="/central-command-x92" element={<SuperAdminPortal />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </>
  );
}

export default App;
